import React, { useMemo } from "react";
import { Cluster, ClusterConfig } from "../config";
import { BlockJs, ViewData } from "../types";
import "./LatencySection.css";

interface LatencySectionProps {
  views: ViewData[];
  selectedCluster: Cluster;
  configs: Record<Cluster, ClusterConfig>;
  onClusterChange?: (cluster: Cluster) => void;
}

const LatencySection: React.FC<LatencySectionProps> = ({
  views,
  selectedCluster,
  configs,
  onClusterChange,
}) => {
  const notarizationTimes = useMemo(
    () =>
      views
        .filter(
          (view) => view.status === "notarized" || view.status === "finalized"
        )
        .map((view) => {
          if (
            view.actualNotarizationLatency !== undefined &&
            view.actualNotarizationLatency > 0
          ) {
            return view.actualNotarizationLatency;
          } else if (view.notarizationTime && view.startTime) {
            const calculatedLatency = view.notarizationTime - view.startTime;
            return calculatedLatency > 0 ? calculatedLatency : null;
          }
          return null;
        })
        .filter((time): time is number => time !== null),
    [views]
  );

  const finalizationTimes = useMemo(
    () =>
      views
        .filter((view) => view.status === "finalized")
        .map((view) => {
          if (
            view.actualFinalizationLatency !== undefined &&
            view.actualFinalizationLatency > 0
          ) {
            return view.actualFinalizationLatency;
          } else if (view.finalizationTime && view.startTime) {
            const calculatedLatency = view.finalizationTime - view.startTime;
            return calculatedLatency > 0 ? calculatedLatency : null;
          }
          return null;
        })
        .filter((time): time is number => time !== null),
    [views]
  );

  const { medianBlockTime, medianTimeToLock, medianTimeToFinalize } =
    useMemo(() => {
      const viewsWithBlocks = views
        .filter(
          (view): view is ViewData & { block: BlockJs } =>
            view.block !== undefined &&
            view.block.height !== undefined &&
            view.block.timestamp !== undefined
        )
        .sort((a, b) => a.block.height - b.block.height);

      const blockTimes: number[] = [];
      if (viewsWithBlocks.length === 0) {
        return {
          medianBlockTime: 0,
          medianTimeToLock: 0,
          medianTimeToFinalize: 0,
        };
      }

      const consecutiveGroups: (typeof viewsWithBlocks)[] = [];
      let currentGroup: typeof viewsWithBlocks = [viewsWithBlocks[0]];

      for (let i = 1; i < viewsWithBlocks.length; i++) {
        const currentBlock = viewsWithBlocks[i].block;
        const prevBlock = currentGroup[currentGroup.length - 1].block;

        if (currentBlock.height === prevBlock.height + 1) {
          currentGroup.push(viewsWithBlocks[i]);
        } else {
          if (currentGroup.length >= 2) {
            consecutiveGroups.push(currentGroup);
          }
          currentGroup = [viewsWithBlocks[i]];
        }
      }

      if (currentGroup.length >= 2) {
        consecutiveGroups.push(currentGroup);
      }

      for (const group of consecutiveGroups) {
        if (group.length >= 3) {
          for (let i = 1; i < group.length; i++) {
            const currentBlock = group[i].block;
            const prevBlock = group[i - 1].block;
            const timeDiff = currentBlock.timestamp - prevBlock.timestamp;
            blockTimes.push(timeDiff);
          }
        }
      }

      const sortedBlockTimes = [...blockTimes].sort((a, b) => a - b);
      const medianBlockTime =
        sortedBlockTimes.length > 0
          ? sortedBlockTimes.length % 2 === 1
            ? sortedBlockTimes[Math.floor(sortedBlockTimes.length / 2)]
            : Math.round(
                (sortedBlockTimes[sortedBlockTimes.length / 2 - 1] +
                  sortedBlockTimes[sortedBlockTimes.length / 2]) /
                  2
              )
          : 0;

      const sortedNotarizationTimes = [...notarizationTimes].sort(
        (a, b) => a - b
      );
      const medianTimeToLock =
        sortedNotarizationTimes.length > 0
          ? sortedNotarizationTimes.length % 2 === 1
            ? sortedNotarizationTimes[
                Math.floor(sortedNotarizationTimes.length / 2)
              ]
            : Math.round(
                (sortedNotarizationTimes[
                  sortedNotarizationTimes.length / 2 - 1
                ] +
                  sortedNotarizationTimes[sortedNotarizationTimes.length / 2]) /
                  2
              )
          : 0;

      const sortedFinalizationTimes = [...finalizationTimes].sort(
        (a, b) => a - b
      );
      const medianTimeToFinalize =
        sortedFinalizationTimes.length > 0
          ? sortedFinalizationTimes.length % 2 === 1
            ? sortedFinalizationTimes[
                Math.floor(sortedFinalizationTimes.length / 2)
              ]
            : Math.round(
                (sortedFinalizationTimes[
                  sortedFinalizationTimes.length / 2 - 1
                ] +
                  sortedFinalizationTimes[sortedFinalizationTimes.length / 2]) /
                  2
              )
          : 0;

      return {
        medianBlockTime,
        medianTimeToLock,
        medianTimeToFinalize,
      };
    }, [views, notarizationTimes, finalizationTimes]);

  const clusterDisplayName =
    selectedCluster === "usa" ? "USA Cluster" : "Global Cluster";

  return (
    <section className="latency-section">
      {/* Header */}
      <div className="latency-section__header">
        <h2 className="latency-section__title">Latency</h2>

      </div>

      {/* Stats Cards */}
      <div className="latency-section__cards">
        {/* Cluster Card */}
        <div className="latency-section__card">
          <div className="latency-section__card-header">Cluster</div>
          <div className="latency-section__card-body latency-section__card-body--peach">
            <div className="latency-section__stat">
              <span className="latency-section__stat-label">Blocktime</span>
              <div className="latency-section__stat-value">
                <span className="latency-section__stat-dot" />
                {medianBlockTime}ms
              </div>
            </div>
          </div>
        </div>

        {/* Browser Card */}
        {/* <div className="latency-section__card">
          <div className="latency-section__card-header">Browser</div>
          <div className="latency-section__card-body latency-section__card-body--peach">
            <div className="latency-section__stat latency-section__stat--half">
              <span className="latency-section__stat-label">Locked</span>
              <span className="latency-section__stat-value">
                {medianTimeToLock}ms
              </span>
            </div>
            <div className="latency-section__stat latency-section__stat--half">
              <span className="latency-section__stat-label">Finalized</span>
              <span className="latency-section__stat-value">
                {medianTimeToFinalize}ms
              </span>
            </div>
          </div>
        </div> */}
      </div>

 
    </section>
  );
};

export default LatencySection;
